/**
 * T056/T121: Prompt List Component
 * Grid/list layout for displaying prompts with keyboard navigation
 */

import { useRef, useCallback } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { PromptCard } from './prompt-card'
import type { PromptResponse } from '@/services/prompts'

interface PromptListProps {
  prompts: PromptResponse[]
}

export function PromptList({ prompts }: PromptListProps) {
  const navigate = useNavigate()
  const listRef = useRef<HTMLDivElement>(null)

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, index: number) => {
      const cards = listRef.current?.querySelectorAll('[data-prompt-card]')
      if (!cards) return

      let nextIndex: number | null = null

      switch (event.key) {
        case 'ArrowRight':
          nextIndex = index + 1
          break
        case 'ArrowLeft':
          nextIndex = index - 1
          break
        case 'ArrowDown':
          // Move to next row (3 columns on lg, 2 on sm)
          nextIndex = index + 3
          break
        case 'ArrowUp':
          nextIndex = index - 3
          break
        case 'Enter':
        case ' ':
          event.preventDefault()
          navigate({ to: '/prompts/$id', params: { id: prompts[index].id } })
          return
        default:
          return
      }

      if (nextIndex !== null && nextIndex >= 0 && nextIndex < cards.length) {
        event.preventDefault()
        ;(cards[nextIndex] as HTMLElement).focus()
      }
    },
    [navigate, prompts]
  )

  return (
    <div
      ref={listRef}
      className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
      role="list"
      aria-label="Prompts list"
    >
      {prompts.map((prompt, index) => (
        <PromptCard
          key={prompt.id}
          prompt={prompt}
          onKeyDown={(e) => handleKeyDown(e, index)}
          tabIndex={0}
          data-prompt-card
        />
      ))}
    </div>
  )
}
